import { supabase } from '@/lib/supabase';
import { Lang } from '@/data/constants';
import { getAttribution, getVisitorId, track } from '@/lib/tracking';

/**
 * SINGLE SOURCE OF TRUTH for sending ANY form submission in the platform to
 * the CRM / GoHighLevel. Every form (registration, contact, quote request,
 * lead request, contractor & subcontractor applications, homeowner forms,
 * support, checkout, future lead-capture forms) must call submitForm().
 *
 * Since the security hardening pass, submitForm() posts to the PROTECTED
 * `crm-capture` edge function, which (server-side):
 *   - rate limits by IP / visitor / email / phone,
 *   - verifies the Cloudflare Turnstile token,
 *   - validates the payload against allow-lists,
 *   - records the consent snapshot,
 *   - upserts the GoHighLevel contact (dedupe on email, then phone),
 *   - queues failures in mkt_crm_queue for automatic retry.
 *
 * GoHighLevel credentials are never exposed to the browser.
 */

export type FormType =
  | 'register-homeowner'
  | 'register-gc'
  | 'register-subcontractor'
  | 'registration-started'
  | 'contact'
  | 'support'
  | 'quote-request'
  | 'lead-request'
  | 'callback-request'
  | 'waitlist'
  | 'premium-setup'
  | 'project-posted'
  | 'work-order-posted'
  | 'lead-purchase'
  | 'subscription'
  | 'checkout-started'
  | 'newsletter';

/** Form type -> GoHighLevel pipeline stage name (create these stages in GHL). */
export const GHL_PIPELINE_STAGE: Record<FormType, string> = {
  'register-homeowner': 'New Registration',
  'register-gc': 'New Registration',
  'register-subcontractor': 'New Registration',
  'registration-started': 'Registration Started',
  contact: 'New Inquiry',
  support: 'Support Request',
  'quote-request': 'New Inquiry',
  'lead-request': 'New Inquiry',
  'callback-request': 'Support Request',
  waitlist: 'New Inquiry',
  'premium-setup': 'Subscriber',
  'project-posted': 'New Project',
  'work-order-posted': 'Work Order Created',
  'lead-purchase': 'Lead Purchased',
  subscription: 'Subscriber',
  'checkout-started': 'Checkout Started',
  newsletter: 'New Inquiry',
};

/**
 * Action -> GoHighLevel tags. Mirrors the TAG_MAP inside the crm-capture function.
 *
 * BRANDING EXCEPTION — READ BEFORE "FIXING" THE CASING HERE.
 * These strings are NOT display copy. They are the tag IDENTIFIERS that already
 * exist on live GoHighLevel contacts and that the deployed `crm-capture` edge
 * function matches on byte-for-byte. Re-casing them to GIGGONE would create a
 * second, parallel set of tags in the CRM and orphan the segmentation on every
 * contact captured so far, while changing nothing a customer can see. They stay
 * as-is on purpose. Anything a human actually reads — sender names, email
 * bodies, SMS/WhatsApp copy, admin labels — goes through BRAND / brandCase()
 * from src/lib/brand.ts and renders as GIGGONE.
 */
export const GHL_TAGS: Record<FormType, string[]> = {

  'register-homeowner': ['GigGone Lead', 'GigGone Customer', 'GigGone Registered'],
  'register-gc': ['GigGone Lead', 'GigGone Contractor', 'GigGone Registered'],
  'register-subcontractor': ['GigGone Lead', 'GigGone Contractor', 'GigGone Registered'],
  'registration-started': ['GigGone Lead', 'GigGone Registration Started'],
  contact: ['GigGone Lead', 'GigGone Contact Request'],
  support: ['GigGone Lead', 'GigGone Contact Request'],
  'quote-request': ['GigGone Lead', 'GigGone Customer'],
  'lead-request': ['GigGone Lead', 'GigGone Contractor'],
  'callback-request': ['GigGone Lead', 'GigGone Contact Request'],
  waitlist: ['GigGone Lead'],
  'premium-setup': ['GigGone Lead', 'GigGone Premium'],
  'project-posted': ['GigGone Lead', 'GigGone Customer'],
  'work-order-posted': ['GigGone Lead', 'GigGone Contractor'],
  'lead-purchase': ['GigGone Customer', 'GigGone Paid'],
  subscription: ['GigGone Customer', 'GigGone Paid'],
  'checkout-started': ['GigGone Lead', 'GigGone Checkout Started'],
  newsletter: ['GigGone Lead'],
};

export interface FormSubmission {
  formType: FormType;
  email: string;
  name?: string;
  phone?: string;
  language: Lang;
  userType?: string;
  companyName?: string;
  plan?: string;
  userId?: string;
  smsOptIn?: boolean;
  /** Explicit marketing consent captured on the form (checkbox / stated notice). */
  consent?: boolean;
  /** Cloudflare Turnstile token — verified server-side inside crm-capture. */
  turnstileToken?: string;
  registrationStatus?: string;
  checkoutStatus?: string;
  paymentStatus?: string;
  paid?: boolean;
  paymentFailed?: boolean;
  /** Extra key/value data forwarded to GoHighLevel custom fields + webhook. */
  fields?: Record<string, string | number | boolean | null | undefined>;
  tags?: string[];
  /** Set false for silent syncs that should not trigger a confirmation email. */
  sendEmail?: boolean;
}

export interface CrmResult {
  ok: boolean;
  crm?: string;
  contactId?: string | null;
  queued?: boolean;
  error?: string;
  message?: string;
}

/**
 * Creates/updates the contact in GoHighLevel and fires the matching workflow.
 * Always non-blocking for the visitor: a CRM outage can never break a form —
 * the payload is queued server-side and retried with exponential backoff.
 */
export const submitForm = async (s: FormSubmission): Promise<CrmResult> => {
  const cleanFields = Object.fromEntries(
    Object.entries(s.fields || {}).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );

  const attr = getAttribution();
  const [firstName, ...rest] = String(s.name || '').trim().split(/\s+/);

  try {
    const { data, error } = await supabase.functions.invoke('crm-capture', {
      body: {
        formType: s.formType,
        email: s.email,
        name: s.name,
        firstName: firstName || undefined,
        lastName: rest.length ? rest.join(' ') : undefined,
        phone: s.phone || undefined,
        companyName: s.companyName,
        userType: s.userType || 'lead',
        plan: s.plan || '',
        userId: s.userId,
        language: s.language,
        smsOptIn: s.smsOptIn === true,
        consent: s.consent !== false,
        turnstileToken: s.turnstileToken,
        registrationStatus: s.registrationStatus,
        checkoutStatus: s.checkoutStatus,
        paymentStatus: s.paymentStatus,
        paid: s.paid === true,
        paymentFailed: s.paymentFailed === true,
        visitorId: getVisitorId(),
        idempotencyKey: `${s.formType}:${(s.email || '').toLowerCase()}:${Date.now().toString(36).slice(0, 7)}`,
        source: s.formType,
        attribution: {
          source: attr.source,
          landingPage: attr.landingPage,
          referrer: attr.referrer,
          utmSource: attr.utmSource,
          utmMedium: attr.utmMedium,
          utmCampaign: attr.utmCampaign,
          utmTerm: attr.utmTerm,
          utmContent: attr.utmContent,
          firstSeen: attr.firstSeen,
          lastSeen: attr.lastSeen,
        },
        tags: [...(GHL_TAGS[s.formType] || []), `stage:${GHL_PIPELINE_STAGE[s.formType]}`, ...(s.tags || [])],
        fields: cleanFields,
      },
    });

    if (error) return { ok: false, error: 'network' };
    track('form_completed', { form: s.formType });
    return (data || { ok: false }) as CrmResult;
  } catch (_e) {
    return { ok: false, error: 'network' };
  }
};
